import { FC, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import {
  ReferralDirection,
  ReferralOnboardingState,
  ReferralSchema,
  ReferralSource,
  ReferralStatus,
  ResourceBookingSchema,
} from '../data/types';
import { useReferralActions } from '../hooks/referralhooks';
import { useLoadMember, useLoadMemberships, useLoadRequests } from '../hooks/memberhooks';
import NewSearch from '../molecules/NewSearch';
import {
  MemberDetailsModal,
  NewMessageModal,
  RequestDetailsModal,
} from '../molecules/AppModal';
import Pagination from './Pagination';
import {
  PARTNER_DELIVERY_STATE_LABELS,
  REFERRAL_CLAIM_STATE_LABELS,
  REFERRAL_DIRECTION_LABELS,
  REFERRAL_ONBOARDING_STATE_LABELS,
  formatContractState,
  formatReferralStatus,
  referralStatusBadgeClass,
} from '../utils/fundingPartners';
import {
  buildFundingPipelineItems,
  FundingPipelineItem,
  fundingPipelineSearchText,
} from '../utils/fundingPipeline';
import { ReactComponent as DeleteIcon } from '../icons/DeleteIcon.svg'
import ReplayIcon from '@mui/icons-material/Replay';

const REFERRAL_STATUS_OPTIONS: ReferralStatus[] = [
  'opted_in',
  'qualified',
  'handed_off',
  'discovery',
  'activated',
  'unqualified',
  'declined',
];

const DIRECTION_FILTERS: Array<ReferralDirection | ''> = [
  '',
  'partner_to_homecook',
  'homecook_to_partner',
];

const SOURCE_FILTERS: Array<ReferralSource | ''> = ['', 'airiwheels', 'homecook'];

const ONBOARDING_STATE_OPTIONS: ReferralOnboardingState[] = [
  'not_started',
  'onboarding_started',
  'facility_information_needed',
  'facility_review_pending',
  'ready_for_next_step',
  'capacity_active',
  'declined',
  'closed',
];

type FundingPipelineSort = 'submitted_desc' | 'submitted_asc' | 'status' | 'source';

type FundingReferralInboxProps = {
  resourceType: string
  resourceRequests?: ResourceBookingSchema[]
  resourceId?: string
  partnerSlug?: string
};

type FundingApplicationActionsProps = {
  application: ResourceBookingSchema
  allApplications: ResourceBookingSchema[]
};

/** Preserve request, member, and messaging actions for linked and legacy applications. */
const FundingApplicationActions: FC<FundingApplicationActionsProps> = ({
  application,
  allApplications,
}) => {
  const [viewRequest, setViewRequest] = useState(false);
  const [viewMember, setViewMember] = useState(false);
  const [sendMessage, setSendMessage] = useState(false);
  const { currentMember } = useLoadMember(application.uid || '');

  return (
    <>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          className="inline-flex h-10 items-center rounded-lg border border-primary-400 px-4 font-raleway text-sm font-semibold text-primary-300"
          onClick={() => setViewRequest(true)}
        >
          Manage request
        </button>
        {currentMember && (
          <button
            type="button"
            className="inline-flex h-10 items-center rounded-lg border border-[#E4EEF1] px-4 font-raleway text-sm font-semibold text-ink"
            onClick={() => setViewMember(true)}
          >
            View member
          </button>
        )}
        {currentMember?.username && (
          <button
            type="button"
            className="inline-flex h-10 items-center rounded-lg border border-[#E4EEF1] px-4 font-raleway text-sm font-semibold text-ink"
            onClick={() => setSendMessage(true)}
          >
            Send message
          </button>
        )}
      </div>
      {viewRequest && application.id && (
        <RequestDetailsModal
          isOpen={viewRequest}
          closeAppModal={() => setViewRequest(false)}
          allResourceRequests={allApplications}
          request={application}
        />
      )}
      {viewMember && currentMember && (
        <MemberDetailsModal
          isOpen={viewMember}
          closeAppModal={() => setViewMember(false)}
          member={currentMember}
        />
      )}
      {sendMessage && currentMember?.username && (
        <NewMessageModal
          username={currentMember.username}
          isOpen={sendMessage}
          closeAppModal={() => setSendMessage(false)}
        />
      )}
    </>
  );
};

const pipelineItemDate = (item: FundingPipelineItem): string =>
  item.application?.created || item.referral?.created || '';

const pipelineItemStatus = (item: FundingPipelineItem): string =>
  item.application?.status
  || item.referral?.referral_state
  || item.referral?.status
  || '';

const pipelineItemSource = (item: FundingPipelineItem): string =>
  item.referral?.source
  || item.referral?.partner_slug
  || 'homecook';

/** One funding pipeline for canonical referrals and linked or legacy applications. */
const FundingReferralInbox: FC<FundingReferralInboxProps> = ({
  resourceType,
  resourceRequests = [],
  resourceId,
  partnerSlug,
}) => {
  const [direction, setDirection] = useState<ReferralDirection | ''>('');
  const [source, setSource] = useState<ReferralSource | ''>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState<FundingPipelineSort>('submitted_desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingDeleteId, setPendingDeleteId] = useState('');
  const {
    updateReferral,
    updateOnboardingState,
    replayEvent,
    deleteReferral,
    isUpdatingReferral,
    isReplayingEvent,
    isDeletingReferral,
  } = useReferralActions();
  const { reloadRequests } = useLoadRequests();
  const { reloadMemberships } = useLoadMemberships();

  const pipelineItems = useMemo(() => buildFundingPipelineItems({
    applications: resourceRequests,
    resourceId,
    partnerSlug,
    includeLegacyApplications: (
      (!direction || direction === 'homecook_to_partner')
      && (!source || source === 'homecook')
    ),
  }).filter((item) => {
    const itemDirection = item.referral?.direction || 'homecook_to_partner';
    if (direction && itemDirection !== direction) return false;
    if (source === 'airiwheels' && itemDirection !== 'partner_to_homecook') return false;
    if (source === 'homecook' && itemDirection !== 'homecook_to_partner') return false;
    return true;
  }), [direction, partnerSlug, resourceId, resourceRequests, source]);

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const matching = term
      ? pipelineItems.filter((item) => fundingPipelineSearchText(item).includes(term))
      : pipelineItems;
    return [...matching].sort((left, right) => {
      if (sort === 'status') {
        return pipelineItemStatus(left).localeCompare(pipelineItemStatus(right));
      }
      if (sort === 'source') {
        return pipelineItemSource(left).localeCompare(pipelineItemSource(right));
      }
      const difference = new Date(pipelineItemDate(left) || 0).getTime()
        - new Date(pipelineItemDate(right) || 0).getTime();
      return sort === 'submitted_asc' ? difference : -difference;
    });
  }, [pipelineItems, searchTerm, sort]);

  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = filteredItems.length > 0 ? (currentPage - 1) * itemsPerPage : 0;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredItems.length);
  const currentItems = filteredItems.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [direction, searchTerm, sort, source]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  if (resourceType !== 'funding') return null;

  /** Patch the partner lifecycle status, then refresh membership requests and approved members. */
  const handleStatusChange = async (referralId: string, status: ReferralStatus) => {
    if (!referralId || !status) return;
    await updateReferral({ referralId, updates: { status } });
    await Promise.all([reloadRequests(), reloadMemberships()]);
  };

  /** Remove a referral from the pipeline once the inline confirmation is accepted. */
  const handleDelete = async (referralId: string) => {
    if (!referralId) return;
    await deleteReferral({ referralId });
    setPendingDeleteId('');
  };

  const handleOnboardingStateChange = async (
    referral: ReferralSchema,
    onboardingState: ReferralOnboardingState,
  ) => {
    if (!referral.id) return;
    await updateOnboardingState({
      referralId: referral.id,
      onboardingState,
    });
  };

  const handleReplay = async (referral: ReferralSchema) => {
    if (!referral.sync?.last_event_id || !referral.partner_slug) return;
    await replayEvent({
      slug: referral.partner_slug,
      eventId: referral.sync.last_event_id,
    });
  };

  return (
    <div className="px-4 pb-4" data-testid="funding-referral-inbox">
      <div className="mb-4 grid gap-3 xl:grid-cols-[minmax(220px,1fr)_auto_auto_auto] xl:items-end">
        <NewSearch
          searchType="member, business, or referral"
          searchTerm={searchTerm}
          changeSearchTerm={setSearchTerm}
        />
        <label className="font-open-sans text-xs text-muted">
          Direction
          <select
            data-testid="referral-direction-filter"
            className="ml-2 h-10 rounded-lg border border-[#E4EEF1] bg-white px-3 text-sm text-ink"
            value={direction}
            onChange={(event) => setDirection(event.target.value as ReferralDirection | '')}
          >
            {DIRECTION_FILTERS.map((value) => (
              <option key={value || 'all'} value={value}>
                {value ? REFERRAL_DIRECTION_LABELS[value] : 'All directions'}
              </option>
            ))}
          </select>
        </label>
        <label className="font-open-sans text-xs text-muted">
          Source
          <select
            data-testid="referral-source-filter"
            className="ml-2 h-10 rounded-lg border border-[#E4EEF1] bg-white px-3 text-sm text-ink"
            value={source}
            onChange={(event) => setSource(event.target.value)}
          >
            {SOURCE_FILTERS.map((value) => (
              <option key={value || 'all'} value={value}>
                {value ? value.replace(/^\w/, (character) => character.toUpperCase()) : 'All sources'}
              </option>
            ))}
          </select>
        </label>
        <label className="font-open-sans text-xs text-muted">
          Sort
          <select
            data-testid="funding-pipeline-sort"
            className="ml-2 h-10 rounded-lg border border-[#E4EEF1] bg-white px-3 text-sm text-ink"
            value={sort}
            onChange={(event) => setSort(event.target.value as FundingPipelineSort)}
          >
            <option value="submitted_desc">Newest submitted</option>
            <option value="submitted_asc">Oldest submitted</option>
            <option value="status">Status</option>
            <option value="source">Source</option>
          </select>
        </label>
      </div>

      <p className="mb-3 text-xs font-open-sans text-muted" data-testid="funding-pipeline-range">
        {filteredItems.length > 0
          ? `Showing ${startIndex + 1} - ${endIndex} of ${filteredItems.length} partner referrals`
          : 'Showing 0 - 0 of 0 partner referrals'}
      </p>

      {currentItems.length === 0 ? (
        <p className="px-2 text-sm text-muted">
          {searchTerm.trim() ? 'No matching partner referrals.' : 'No partner referrals yet.'}
        </p>
      ) : (
        <div className="space-y-3">
          {currentItems.map(({ id, referral, application, isLegacyApplication }) => (
            <div key={id} className="partner-section-card p-4" data-testid="referral-row">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-raleway font-semibold text-ink">
                    {referral?.external_referral_id
                      || referral?.referral_code
                      || application?.resource_name
                      || 'HomeCook funding request'}
                  </p>
                  <p className="mt-0.5 text-xs font-open-sans text-muted">
                    {isLegacyApplication
                      ? 'HomeCook · legacy application'
                      : pipelineItemSource({ id, referral, application, isLegacyApplication })}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-raleway font-semibold ${referralStatusBadgeClass(referral?.referral_state || referral?.status)}`}>
                    {referral
                      ? formatReferralStatus(referral.referral_state || referral.status)
                      : formatContractState(application?.status, {})}
                  </span>
                  <span className="rounded-full bg-primary-tint px-2.5 py-1 text-[11px] font-raleway font-semibold text-primary-100">
                    {REFERRAL_DIRECTION_LABELS[referral?.direction || 'homecook_to_partner']}
                  </span>
                </div>
              </div>

              {application && (
                <div className="mt-3 grid gap-3 rounded-xl border border-[#E4EEF1] bg-[#F8FCFD] p-3 text-sm font-open-sans sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted font-raleway">Member</p>
                    <p className="text-ink">{application.user_full_name || '—'}</p>
                    <p className="text-xs text-muted">{application.username || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted font-raleway">Funding resource</p>
                    <p className="text-ink">{application.resource_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted font-raleway">Submitted</p>
                    <p className="text-ink">
                      {application.created ? moment.utc(application.created).fromNow() : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted font-raleway">Request status</p>
                    <p className="text-ink">{formatContractState(application.status, {})}</p>
                  </div>
                </div>
              )}

              {referral && (
                <div className="mt-3 grid gap-3 text-sm font-open-sans sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted font-raleway">Consent</p>
                    <p className="text-ink">{referral.consent?.version || '—'}</p>
                    <p className="text-xs text-muted">{referral.consent?.granted_at || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted font-raleway">Claim state</p>
                    <p className="text-ink">
                      {formatContractState(referral.claim_state, REFERRAL_CLAIM_STATE_LABELS)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted font-raleway">Delivery state</p>
                    <p className="text-ink">
                      {formatContractState(referral.sync?.delivery_status, PARTNER_DELIVERY_STATE_LABELS)}
                    </p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="mb-1 text-xs uppercase tracking-wide text-muted font-raleway">Public onboarding state</p>
                    {referral.direction === 'partner_to_homecook' ? (
                      <select
                        value={referral.onboarding_state || 'not_started'}
                        disabled={isUpdatingReferral}
                        onChange={(event) => handleOnboardingStateChange(
                          referral,
                          event.target.value as ReferralOnboardingState,
                        )}
                        className="h-10 w-full rounded-lg border border-[#E4EEF1] bg-white px-2 text-sm text-ink"
                        aria-label="Public onboarding state"
                      >
                        {ONBOARDING_STATE_OPTIONS.map((state) => (
                          <option key={state} value={state}>
                            {REFERRAL_ONBOARDING_STATE_LABELS[state]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-ink">
                        {formatContractState(referral.onboarding_state, REFERRAL_ONBOARDING_STATE_LABELS)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-3 flex flex-wrap items-end gap-3">
                <div className="min-w-[200px] flex-1">
                  <p className="mb-1 text-xs uppercase tracking-wide text-muted font-raleway">Referral state</p>
                  <select
                    value={referral?.status || ''}
                    disabled={isUpdatingReferral || isDeletingReferral}
                    onChange={(event) => handleStatusChange(id, event.target.value as ReferralStatus)}
                    className="h-10 w-full rounded-lg border border-[#E4EEF1] bg-white px-2 text-sm text-ink"
                    aria-label="Referral state"
                  >
                    {!referral?.status && <option value="">Not set</option>}
                    {REFERRAL_STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>{formatReferralStatus(status)}</option>
                    ))}
                  </select>
                </div>
                {pendingDeleteId === id ? (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={isDeletingReferral}
                      className="inline-flex h-10 items-center rounded-lg bg-[#C0392B] px-4 font-raleway text-sm font-semibold text-white"
                      onClick={() => handleDelete(id)}
                    >
                      Confirm remove
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-10 items-center rounded-lg border border-[#E4EEF1] px-4 font-raleway text-sm font-semibold text-ink"
                      onClick={() => setPendingDeleteId('')}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={isDeletingReferral}
                    className="inline-flex h-10 items-center rounded-lg border border-[#C0392B] px-4 font-raleway text-sm font-semibold text-[#C0392B]"
                    onClick={() => setPendingDeleteId(id)}
                    aria-label={`Remove referral ${id}`}
                    title="Remove referral"
                  >
                    <DeleteIcon className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-start justify-between gap-3">
                {application && (
                  <FundingApplicationActions
                    application={application}
                    allApplications={resourceRequests}
                  />
                )}
                {referral?.sync?.last_event_id
                  && referral.sync.delivery_status !== 'delivered'
                  && (
                    <button
                      type="button"
                      disabled={isReplayingEvent}
                      className="mt-3 inline-flex h-10 items-center rounded-lg border border-primary-400 px-4 font-raleway text-sm font-semibold text-primary-300"
                      onClick={() => handleReplay(referral)}
                      aria-label="Replay"
                      title="Replay"
                    >
                      <ReplayIcon className="w-4 h-4" />
                    </button>
                  )}
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredItems.length > itemsPerPage && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredItems.length}
            itemsPerPage={itemsPerPage}
          />
        </div>
      )}
    </div>
  );
};

export default FundingReferralInbox;
